// eslint-disable-next-line no-restricted-imports
import { Stack, Box, CircularProgress } from "@mui/material";

import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { ChevronRight } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

export type CardWithIconProps = {
  icon: IconElement;
  intensity: "warning" | "positive" | "neutral" | "brand";
  title: string;
  description?: string;
  onClick: () => void;
  loading?: boolean;
  "data-test"?: string;
};

export const CardWithIcon = ({
  icon,
  intensity,
  title,
  description,
  onClick,
  loading = false,
  "data-test": dataTest,
}: CardWithIconProps) => {
  return (
    <Box
      data-test={dataTest}
      onClick={loading ? undefined : onClick}
      sx={(theme) => ({
        padding: theme.spacing(2, 3),
        border: `1px solid ${theme.palette.border.neutral.quiet}`,
        borderRadius: theme.spacing(1),
        transition: theme.transitions.create(["background-color", "border-color"]),
        "&:hover": {
          cursor: loading ? "default" : "pointer",
          backgroundColor: loading ? "inherit" : theme.palette.surface.neutral.supershy,
        },
        "&:active": {
          backgroundColor: loading ? "inherit" : theme.palette.surface.brand.quiet,
        },
      })}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        {/* Icon */}
        <Box
          sx={(theme) => ({
            borderRadius: theme.radius(1),
            height: 32,
            width: 32,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: theme.palette.surface[intensity].quiet,
          })}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: (theme) => theme.palette.text[intensity].intense }} />
          ) : (
            <DashboardIcon size="medium" vibe={`text-${intensity}-intense`}>
              {icon}
            </DashboardIcon>
          )}
        </Box>

        {/* Title and description */}
        <Stack direction="column" sx={{ flex: 1 }}>
          <DashboardTypography variant="subtitle1">{title}</DashboardTypography>

          {description && (
            <DashboardTypography variant="body2" sx={{ color: (theme) => theme.palette.text.neutral.quiet }}>
              {description}
            </DashboardTypography>
          )}
        </Stack>

        {/* Arrow */}
        <DashboardIcon sx={{ color: (theme) => theme.palette.text.neutral.moderate }}>
          <ChevronRight />
        </DashboardIcon>
      </Stack>
    </Box>
  );
};
